import {
  AHButton,
  AHSwitch,
  AHText,
  Box,
  Card,
  Input,
  TopTab,
  TopTabItem,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, { useCallback, useState } from "react";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { useAlert } from "../../common/Alert";
import { StoreAdminNavigationScreenProps } from "../StoreAdmin/StoreAdminNavigation";

import { ActivateFeaturesCreateOrgMutation } from "./__generated__/ActivateFeaturesCreateOrgMutation.graphql";
import { ActivateFeaturesFlagEditQuery } from "./__generated__/ActivateFeaturesFlagEditQuery.graphql";
import { ActivateFeaturesEnableFlagMutation } from "./__generated__/ActivateFeaturesEnableFlagMutation.graphql";
import { ActivateFeaturesDisableFlagMutation } from "./__generated__/ActivateFeaturesDisableFlagMutation.graphql";
import { ActivateFeaturesPLU } from "./ActivateFeaturesPLU";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";

type Props = StoreAdminNavigationScreenProps<"SuperAdminPanel">;

export const SuperAdminPanel = (props: Props) => {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => <ActivateFeaturesCmp {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
};
function ActivateFeaturesCmp(props: Props & { fetchKey: string }) {
  const layout = useLayoutContext();
  const insets = useSafeAreaInsets();

  useNavigationHeader({
    type: "DONE",
    onDone: () => {
      props.navigation.goBack();
    },
    title: "Super Admin Panel",
    defaultContentStyle: true,
    disableBorder: true,
  });

  const [activeTab, setActiveTab] = useState<"featureFlags" | "newOrg" | "plu">(
    "featureFlags"
  );
  const theme = useTheme();

  const renderChildren = useCallback(() => {
    switch (activeTab) {
      case "featureFlags":
        return <FlagEdit />;
      case "newOrg":
        return <CreateNewOrg />;
      case "plu":
        return (
          <ActivateFeaturesPLU
            fetchKey={props.fetchKey}
            navigation={props.navigation}
            route={props.route}
          />
        );
    }
  }, [activeTab, props.fetchKey, props.navigation, props.route]);

  return (
    <>
      <Box
        spaceTop="small"
        color="surface"
        borderColor="border-primary"
        style={{ borderBottomWidth: 1 }}
      >
        <TopTab isCompact={false}>
          <TopTabItem
            onPress={() => {
              setActiveTab("featureFlags");
            }}
            selected={activeTab === "featureFlags"}
          >
            Feature Flags
          </TopTabItem>
          <TopTabItem
            onPress={() => {
              setActiveTab("newOrg");
            }}
            selected={activeTab === "newOrg"}
          >
            New Org
          </TopTabItem>
          <TopTabItem
            onPress={() => {
              setActiveTab("plu");
            }}
            selected={activeTab === "plu"}
          >
            PLU
          </TopTabItem>
        </TopTab>
      </Box>
      <ScrollView
        style={{
          backgroundColor: theme.background,
        }}
        contentContainerStyle={{
          paddingHorizontal: layout.space.medium,
          paddingTop: layout.space.large,
          paddingBottom: Math.max(
            layout.space.large,
            insets.bottom + layout.space.small
          ),
        }}
      >
        {renderChildren()}
      </ScrollView>
    </>
  );
}

function CreateNewOrg() {
  const [orgName, setOrgName] = useState("");
  const [email, setEmail] = useState("");
  const [createOrg, createOrgLoading] =
    useMutation<ActivateFeaturesCreateOrgMutation>(graphql`
      mutation StoreSuperAdminPanelCreateOrgMutation(
        $orgName: String!
        $email: String!
      ) {
        _adminCreateOrg(email: $email, name: $orgName)
      }
    `);

  const Alert = useAlert();
  return (
    <Card>
      <Box spaceVertical="medium">
        <Input
          placeholder="Orgnamn"
          value={orgName}
          onChangeText={setOrgName}
        />
        <Box spaceVertical="medium" />
        <Input placeholder="email" value={email} onChangeText={setEmail} />
        <Box spaceVertical="medium" />
        <AHButton
          themeType="primary"
          loading={createOrgLoading}
          onPress={() => {
            createOrg({
              variables: {
                orgName,
                email,
              },
              onCompleted: () => {
                Alert.alert("Organisation skapad!");
                setOrgName("");
                setEmail("");
              },
              onError: () => {
                Alert.alert("Fel inträffade");
              },
            });
          }}
          title="Skapa ny organisation"
        />
      </Box>
    </Card>
  );
}

function FlagEdit() {
  const data = useLazyLoadQuery<ActivateFeaturesFlagEditQuery>(
    graphql`
      query StoreSuperAdminPanelFlagEditQuery {
        store {
          id
          flags
          _adminAllStoreFlags
        }
      }
    `,
    {}
  );
  const storeId = data?.store?.id;
  const [enableFlag] = useMutation<ActivateFeaturesEnableFlagMutation>(graphql`
    mutation StoreSuperAdminPanelEnableFlagMutation(
      $storeId: ID!
      $flag: StoreFlag!
    ) @raw_response_type {
      store(id: $storeId) {
        _adminDisableFlag(flag: $flag) {
          flags
        }
      }
    }
  `);
  const [disableFlag] = useMutation<ActivateFeaturesDisableFlagMutation>(
    graphql`
      mutation StoreSuperAdminPanelDisableFlagMutation(
        $storeId: ID!
        $flag: StoreFlag!
      ) @raw_response_type {
        store(id: $storeId) {
          _adminEnableFlag(flag: $flag) {
            flags
          }
        }
      }
    `
  );
  if (!storeId) {
    return null;
  }
  return (
    <Card>
      <Box spaceVertical="medium">
        {data?.store?._adminAllStoreFlags.map((flag) => (
          <Box
            spaceVertical="small"
            style={{ flexDirection: "row" }}
            key={flag}
          >
            <AHSwitch
              value={data?.store?.flags.includes(flag)}
              onValueChange={(e) => {
                if (!e) {
                  enableFlag({
                    variables: { flag, storeId },
                    optimisticResponse: {
                      store: {
                        _adminDisableFlag: {
                          id: storeId,
                          flags: [...data?.store?.flags, flag],
                        },
                      },
                    },
                  });
                } else {
                  disableFlag({
                    variables: { flag, storeId },
                    optimisticResponse: {
                      store: {
                        _adminEnableFlag: {
                          id: storeId,
                          flags: data?.store?.flags.filter((f) => f !== flag),
                        },
                      },
                    },
                  });
                }
              }}
            />
            <AHText spaceLeft="medium" type="subtitle">
              {flag}
            </AHText>
          </Box>
        ))}
      </Box>
    </Card>
  );
}
